// modal.ts
class Modal {
    private modals: NodeListOf<HTMLElement>;
    private openButtons: NodeListOf<HTMLElement>;
    private closeButtons: NodeListOf<HTMLElement>;

    private originalWrapperStyles: Map<HTMLElement, { overflow: string; pointerEvents: string; touchAction: string; }> = new Map();
    private originalBodyPaddingRight: string | null = null;

    private modalOpenEl: HTMLElement | null = null;

    // bound handlers
    private boundPreventScroll: (e: Event) => void;
    private boundPreventPointer: (e: Event) => void;
    private boundKeydown: (e: KeyboardEvent) => void;

    constructor() {
        this.modals = document.querySelectorAll('.modal');
        this.openButtons = document.querySelectorAll('.open-modal');
        this.closeButtons = document.querySelectorAll('.close-modal, .button-modal-close');

        this.boundPreventScroll = this.preventScroll.bind(this);
        this.boundPreventPointer = this.preventPointerDown.bind(this);
        this.boundKeydown = this.onKeydown.bind(this);

        this._init();
    }

    private _init(): void {
        this.openButtons.forEach((button: HTMLElement) => {
            button.addEventListener('click', (e: Event) => {
                const modalId = (button as HTMLElement).getAttribute('data-modal');
                if (modalId) this.open(modalId, button);
            });
        });

        this.closeButtons.forEach((button: HTMLElement) => {
            button.addEventListener('click', () => this.close());
        });

        this.modals.forEach((modal: HTMLElement) => {
            modal.addEventListener('click', (e: MouseEvent) => {
                const modalContent = modal.querySelector<HTMLElement>('.modal-content');
                if (!modalContent) return;

                // если клик произошёл вне modal-content
                if (!modalContent.contains(e.target as Node)) {
                    this.close();
                }
            });
        });

        document.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Escape') this.close();
        });
    }

    public open(modalId: string, triggerButton?: HTMLElement): void {
        this.close(); // закроем другие

        const modal = document.getElementById(modalId);
        if (!modal) return;

        modal.classList.add('show');
        document.body.classList.add('modal-open');

        this.modalOpenEl = modal;
        this.disablePageScrollExceptModal(modal);

        if (triggerButton) {
            const doctorName = triggerButton.getAttribute("data-name");
            const serviceName = triggerButton.getAttribute("data-service");

            if (doctorName) {
                const doctorItems = modal.querySelectorAll<HTMLElement>(
                    `.form-item_doctor .form-popup-item-text`
                );
                doctorItems.forEach(item => {
                    if (item.textContent?.trim() === doctorName.trim()) {
                        item.click();
                    }
                });
            }

            if (serviceName) {
                const serviceItems = modal.querySelectorAll<HTMLElement>(
                    `.form-item_service .form-popup-item-text`
                );
                serviceItems.forEach(item => {
                    if (item.textContent?.trim() === serviceName.trim()) {
                        item.click();
                    }
                });
            }
        }
    }



    public close(): void {
        this.modals.forEach(m => m.classList.remove('show'));
        document.body.classList.remove('modal-open');

        this.modalOpenEl = null;
        this.restorePageScroll();
    }

    // ---------------------- helpers ----------------------

    // Находит "scroll wrappers" внутри [data-simplebar] (несколько вариантов селекторов)
    private getSimplebarWrappers(): HTMLElement[] {
        const containers = Array.from(document.querySelectorAll<HTMLElement>('[data-simplebar]'));
        const results: HTMLElement[] = [];

        containers.forEach(container => {
            const wrapper =
                container.querySelector<HTMLElement>('.simplebar-content-wrapper') ||
                container.querySelector<HTMLElement>('.simplebar-content') ||
                container.querySelector<HTMLElement>('.simplebar-scroll-content') ||
                container; // fallback: сам контейнер

            if (wrapper) results.push(wrapper);
        });

        return results;
    }

// ---------------------- helpers ----------------------

// Отключаем скролл и интерактивность вне модалки
    private disablePageScrollExceptModal(excludeModal: HTMLElement): void {
        const wrappers = this.getSimplebarWrappers();

        // Блокируем SimpleBar вне модалки
        wrappers.forEach(w => {
            if (excludeModal.contains(w)) return;
            this.originalWrapperStyles.set(w, {
                overflow: w.style.overflow || '',
                pointerEvents: w.style.pointerEvents || '',
                touchAction: w.style.touchAction || ''
            });
            w.style.overflow = 'hidden';
        });

        // wheel + touchmove → блокируем скролл вне модалки
        window.addEventListener('wheel', this.boundPreventScroll, { passive: false, capture: true });
        window.addEventListener('touchmove', this.boundPreventScroll, { passive: false, capture: true });

        // pointerdown → блокируем клики вне модалки
        window.addEventListener('pointerdown', this.boundPreventPointer, { passive: false, capture: true });

        // клавиатура
        document.addEventListener('keydown', this.boundKeydown, { capture: true });
    }

// Восстанавливаем стили и убираем слушатели
    private restorePageScroll(): void {
        // восстановим wrappers
        this.originalWrapperStyles.forEach((prev, el) => {
            try {
                el.style.overflow = prev.overflow || '';
                el.style.pointerEvents = prev.pointerEvents || '';
                el.style.touchAction = prev.touchAction || '';
            } catch (err) {
                // noop
            }
        });
        this.originalWrapperStyles.clear();

        // восстановим body padding
        if (this.originalBodyPaddingRight !== null) {
            document.body.style.paddingRight = this.originalBodyPaddingRight;
            this.originalBodyPaddingRight = null;
        }

        // удалим слушатели
        window.removeEventListener('wheel', this.boundPreventScroll, { capture: true } as any);
        window.removeEventListener('touchmove', this.boundPreventScroll, { capture: true } as any);
        window.removeEventListener('pointerdown', this.boundPreventPointer, { capture: true } as any);
        document.removeEventListener('keydown', this.boundKeydown, { capture: true } as any);
    }

    // Блокирует wheel/touchmove (вызывается в capture). Если таргет внутри модалки — разрешаем.
    private preventScroll(e: Event): void {
        if (!this.modalOpenEl) return;

        const target = e.target as Node | null;
        if (target && this.modalOpenEl.contains(target)) {
            // если событие внутри модалки — разрешаем
            return;
        }

        e.preventDefault();
        e.stopPropagation();
    }

    // pointerdown: предотвращаем клики по трекам/track'ам вне модалки
    private preventPointerDown(e: Event): void {
        if (!this.modalOpenEl) return;

        const target = e.target as Node | null;
        if (target && this.modalOpenEl.contains(target)) return;

        e.preventDefault();
        e.stopPropagation();
    }

    // Блокировка клавиш, которые скроллят страницу
    private onKeydown(e: KeyboardEvent): void {
        if (!this.modalOpenEl) return;

        const target = e.target as HTMLElement | null;
        // если фокус внутри модалки — разрешаем
        if (target && this.modalOpenEl.contains(target)) return;

        // клавиши, мешающие: Space, PageUp/PageDown, ArrowUp/Down, Home/End
        const blocked = [' ', 'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
        if (blocked.includes(e.key)) {
            e.preventDefault();
            e.stopPropagation();
        }
    }
}

// Экспорт
export const modal = new Modal();
(window as any).modal = modal;
